/**
 * Hover-help registry. Every interactive element carries data-help="key";
 * `text` says what it does, `how` explains how it works or how to use it.
 */
export interface HelpEntry {
  title: string;
  text: string;
  how?: string;
  keys?: string;
}

export const HELP: Record<string, HelpEntry> = {
  // ------------------------------------------------------------ app & menus
  'app.about': {
    title: 'Aperto',
    text: 'Browser-based optical inspection and intensity profiling. Everything runs locally in your browser: images never leave this machine.',
    how: 'Load an image (drop, paste, open or capture), draw a line (L) and read the profile and metrics. Open Help (?) for a quick start.',
  },
  'menu.file': {
    title: 'File menu',
    text: 'Open images into the four slots, paste from the clipboard, save or load sessions and open the export panel.',
    how: 'Slot A is measured; B is a reference for overlays and A/B comparisons; dark and flat frames feed the correction stage of the photometric pipeline.',
  },
  'menu.panels': {
    title: 'Panels',
    text: 'Show or hide any panel and restore the default arrangement.',
    how: 'Panels float: drag a title bar to move (edges snap to other panels), drag an edge or corner to resize, double-click a title to collapse. The layout is remembered.',
  },
  'panel.collapse': {
    title: 'Collapse / expand',
    text: 'Fold the panel to its title bar.',
    how: 'Double-clicking the title bar does the same.',
  },
  'panel.close': { title: 'Close panel', text: 'Hide this panel.', how: 'Re-open it from the Panels menu or its toolbar button.' },

  // ------------------------------------------------------------ panels
  'panel.image': {
    title: 'Image viewport',
    text: 'The inspection aperture: pan, zoom, draw and edit ROIs. The status bar shows zoom, cursor position and the measured value under the cursor.',
    how: 'Wheel = zoom at cursor · drag empty space or hold Space = pan · double-click empty space = fit · zoom beyond ~5000 % to see pixel values.',
  },
  'panel.profile': {
    title: 'Line profile',
    text: 'Cross-sectional profile of the selected line: distance along the line versus the measured quantity, with band envelope, markers, overlays and pinned references.',
    how: 'Hover to read values (the position is mirrored on the image), drag horizontally to zoom, wheel to zoom, double-click to reset.',
  },
  'panel.sources': {
    title: 'Sources',
    text: 'The four image slots (A measured, B reference, dark, flat), live screen capture, and RAW decoding options.',
    how: 'Drop a file on a slot to load it there. Each slot shows format, bit depth and the detected colour encoding.',
  },
  'panel.photometry': {
    title: 'Photometry',
    text: 'How pixel values become physical quantities: linearisation (EOTF), colour primaries, dark/flat correction and the measured quantity.',
    how: 'Automatic mode uses metadata from the file (ICC profile, PNG chunks, format conventions). Switch to Manual to override.',
  },
  'panel.display': {
    title: 'Display',
    text: 'How the image is shown: natural colour, the measured quantity in grey, or false colour; window/level, overlays and iso-contours.',
    how: 'Display settings never change measured values: they only map numbers to screen colours.',
  },
  'panel.metrics': {
    title: 'Metrics',
    text: 'Optical metrics of the selected line profile: extremes, statistics, uniformity, contrast, baseline, peaks with full widths, and edge transition.',
    how: 'Open “Analysis settings” to choose the baseline model, peak polarity, multi-peak detection and width levels.',
  },
  'panel.rois': {
    title: 'ROIs',
    text: 'All regions of interest with colour, name, visibility, lock, numeric geometry and — for areas and points — full statistics and colorimetry.',
    how: 'Click a row to select. Edit coordinates numerically for exact placement (image pixel coordinates; pixel centres are at .5).',
  },
  'panel.inspector': {
    title: 'Pixel inspector',
    text: 'Everything about the pixel under the cursor: raw code values, linear RGB, XYZ, chromaticity, CCT, and a numeric loupe of the neighbourhood.',
    how: 'Press I to freeze the position so you can move the mouse elsewhere.',
    keys: 'I',
  },
  'panel.calibration': {
    title: 'Calibration',
    text: 'Photometric calibration (relative values → cd/m² or any unit) and spatial calibration (pixels → mm, µm, degrees…).',
    how: 'Photometric: place ROIs on patches of known luminance and add them as reference points. Spatial: draw a line over a known distance.',
  },
  'panel.analysis': {
    title: 'Analysis',
    text: 'Spatial-frequency spectrum of the profile (pitch, periodic non-uniformity, moiré) and slanted-edge MTF (sharpness of optics/displays).',
  },
  'panel.export': {
    title: 'Export',
    text: 'CSV profiles and metrics, JSON report, PNG/SVG charts, annotated images, float TIFF/PFM of the measurement plane, and session files.',
    keys: '⌘E',
  },
  'panel.help': { title: 'Help', text: 'Quick start, keyboard shortcuts and a searchable reference of every control.', keys: '?' },

  // ------------------------------------------------------------ tools
  'tool.select': {
    title: 'Select / edit',
    text: 'Select, move and reshape ROIs.',
    how: 'Drag a handle to reshape, drag the body to move, drag empty space to pan. Arrow keys nudge (Shift ×10, Alt ×0.1 px). Delete removes.',
    keys: 'V',
  },
  'tool.pan': {
    title: 'Pan',
    text: 'Drag to move the image.',
    how: 'Holding Space or the middle mouse button pans with any tool.',
    keys: 'H',
  },
  'tool.line': {
    title: 'Line profile',
    text: 'Draw a measurement line; its intensity profile appears in the Line profile panel.',
    how: 'Drag from start to end. Shift constrains the angle to 15° steps. Afterwards drag the round side handles to set the band width that is averaged perpendicular to the line.',
    keys: 'L',
  },
  'tool.rect': {
    title: 'Rectangle ROI',
    text: 'Area for statistics, uniformity, colorimetry and slanted-edge MTF.',
    how: 'Drag corner to corner. Shift = square, Alt = from centre.',
    keys: 'R',
  },
  'tool.ellipse': {
    title: 'Ellipse ROI',
    text: 'Elliptical area for statistics (e.g. a spot-meter field).',
    how: 'Drag the bounding box. Shift = circle, Alt = from centre.',
    keys: 'E',
  },
  'tool.polygon': {
    title: 'Polygon ROI',
    text: 'Free-form area for statistics.',
    how: 'Click to add vertices; click the first vertex, double-click or press Enter to close. Backspace removes the last vertex, Esc cancels.',
    keys: 'P',
  },
  'tool.point': {
    title: 'Point / spot',
    text: 'Single pixel or small square window for spot readings and calibration references.',
    how: 'Click to place. Set the window size in the ROI panel.',
    keys: 'O',
  },
  'tool.calibrate': {
    title: 'Spatial calibration line',
    text: 'Draw along a feature of known size to set the pixel scale.',
    how: 'After drawing, enter the known length and unit. All distances, widths and frequencies then use that unit.',
    keys: 'C',
  },
  'tool.snap': {
    title: 'Snap to pixel centres',
    text: 'Snap line end points and point ROIs to pixel centres (x.5, y.5) for interpolation-free sampling, and rectangle / ellipse / polygon edges to pixel boundaries so they cover whole pixels.',
    how: 'Holding ⌘/Ctrl while drawing or dragging inverts the setting temporarily.',
    keys: 'S',
  },
  'history.undo': { title: 'Undo', text: 'Undo the last ROI change.', keys: '⌘Z' },
  'history.redo': { title: 'Redo', text: 'Redo the last undone ROI change.', keys: '⇧⌘Z' },
  'zoom.fit': { title: 'Fit to view', text: 'Show the whole image.', keys: 'F' },
  'zoom.one': { title: 'Actual pixels (1:1)', text: 'One image pixel per physical screen pixel.', keys: '1' },
  'zoom.in': { title: 'Zoom in', text: 'Magnify around the centre.', keys: '+' },
  'zoom.out': { title: 'Zoom out', text: 'Reduce around the centre.', keys: '−' },
  'status.zoom': { title: 'Zoom', text: 'Magnification relative to physical screen pixels (100 % = 1:1).' },

  // ------------------------------------------------------------ sources & capture
  'slot.A': {
    title: 'Image A',
    text: 'The image being measured. All profiles, metrics and statistics read from A (unless the view is switched to B or a comparison).',
  },
  'slot.B': {
    title: 'Image B',
    text: 'Reference image. Its profile along the same line is overlaid (dashed), and the Display panel can show A−B, A/B or (A−B)/B.',
    how: 'Use it for before/after, reference-vs-sample or two display states. Sizes must match for difference and ratio views.',
  },
  'slot.dark': {
    title: 'Dark frame',
    text: 'An exposure with no light (lens cap / display off). Subtracting it removes sensor offset and fixed-pattern noise.',
    how: 'Enable “Dark frame” in Photometry → Dark & flat correction. It must match the image size and capture settings.',
  },
  'slot.flat': {
    title: 'Flat field',
    text: 'An image of a uniform source. Dividing by it (normalised to mean 1) removes vignetting and pixel-response non-uniformity.',
    how: 'Enable “Flat-field correction” in Photometry. If a dark frame is active it is also subtracted from the flat.',
  },
  'slot.open': { title: 'Open file', text: 'Choose an image file for this slot.', how: 'You can also drop a file directly on the slot.' },
  'slot.clear': { title: 'Clear slot', text: 'Remove the image from this slot.' },
  'slot.encoding': {
    title: 'Detected encoding',
    text: 'What the decoder found about the colour encoding (ICC profile, PNG cICP/sRGB/gAMA/cHRM chunks, format conventions) and what was assumed.',
    how: 'This drives Automatic mode in the Photometry panel.',
  },
  'capture.section': {
    title: 'Screen capture',
    text: 'Capture a screen, window or browser tab live, then freeze a frame for stable measurement.',
    how: 'Captured frames pass through the browser’s video pipeline and are delivered as sRGB. They describe what the display was asked to show, not emitted luminance.',
  },
  'capture.start': {
    title: 'Start capture',
    text: 'Choose a screen, window or tab to show live in the viewport.',
    how: 'Position your content, then press Freeze (G).',
  },
  'capture.freeze': {
    title: 'Freeze frame',
    text: 'Stop live updating and hold the current capture in image A (averaging several frames if set).',
    how: 'Averaging N frames reduces video-compression noise and dithering by about √N. The live stream keeps running for further grabs.',
    keys: 'G',
  },
  'capture.freezeB': { title: 'Freeze into B', text: 'Grab the capture into image B, e.g. as a reference for A/B comparison.', keys: '⇧G' },
  'capture.resume': {
    title: 'Back to live',
    text: 'Show the live capture again and resume live measurements (they replace the frozen image in A).',
  },
  'capture.liveMeasure': {
    title: 'Live measurements',
    text: 'While the feed is live (not frozen), every profile, metric, ROI statistic and the inspector update continuously from the video.',
    how: 'Frames are sampled as exact 8-bit codes as fast as the measurements can be recomputed (up to the video frame rate); the achieved rate is shown in the status bar. Freeze (G) to hold a frame, optionally averaged.',
  },
  'capture.liveRate': {
    title: 'Live measurement rate',
    text: 'How often measurements are refreshed from the live feed. A static screen delivers new frames only when its content changes.',
  },
  'capture.stop': { title: 'Stop capture', text: 'End screen sharing.' },
  'capture.average': {
    title: 'Frames to average',
    text: 'Number of consecutive video frames averaged when freezing.',
    how: '1 keeps exact 8-bit codes; more frames produce a float image of mean codes. A pixel counts as saturated only if every frame was at 255.',
  },
  'raw.section': {
    title: 'Camera RAW decoding',
    text: 'Options for RAW / DNG files, decoded by LibRaw (WebAssembly) to linear 16-bit data in sRGB primaries.',
  },
  'raw.wb': {
    title: 'White balance',
    text: 'Channel multipliers applied by the RAW decoder.',
    how: 'As shot uses the camera’s recorded balance; None keeps the sensor’s native balance (best for strictly radiometric work with your own calibration).',
  },
  'raw.quality': {
    title: 'Demosaicing',
    text: 'Algorithm used to reconstruct full RGB from the colour filter array.',
    how: 'AHD is a good default; bilinear is fastest but softer at edges.',
  },
  'raw.half': {
    title: 'Half size',
    text: 'Combine each 2×2 Bayer quad into one pixel: no interpolation artefacts, half resolution, lower noise.',
  },

  // ------------------------------------------------------------ photometry
  'photometry.quantity': {
    title: 'Measured quantity',
    text: 'The number every profile, metric and statistic reports.',
    how: 'Y is CIE relative luminance (photometric). R/G/B are linear channels. L* is perceptual lightness. x, y, u′, v′, CCT and Duv are colorimetric. Code values and luma are raw encoded numbers (no linearisation).',
  },
  'photometry.resolved': {
    title: 'Resulting unit',
    text: 'Unit of the measured quantity after linearisation and calibration: “rel.” means relative (1.0 = encoded white).',
  },
  'photometry.eotf': {
    title: 'Linearisation (EOTF)',
    text: 'Converts encoded code values into values proportional to light.',
    how: 'Averaging, interpolation and ratios are only physically meaningful on linear values, so all sampling happens after this step.',
  },
  'photometry.mode': {
    title: 'Automatic / Manual',
    text: 'Automatic follows the file’s metadata (shown below); Manual lets you choose explicitly.',
  },
  'photometry.transfer': {
    title: 'Transfer function',
    text: 'The decoding curve applied to normalised code values.',
    how: 'sRGB: IEC 61966-2-1 piecewise curve (most images and screenshots). Gamma: pure power law. BT.1886: reference display EOTF with black level. PQ/HLG: HDR video (PQ yields absolute cd/m²). Linear: data already linear (RAW, EXR, PFM, scientific TIFF).',
  },
  'photometry.gamma': {
    title: 'Gamma',
    text: 'Exponent of the power law: linear = code^γ.',
    how: 'Typical: 2.2 (PC displays), 2.4 (BT.1886 video), 1.8 (legacy Mac).',
  },
  'photometry.bt1886': {
    title: 'BT.1886 black level',
    text: 'Display black-to-white luminance ratio Lb/Lw used by the BT.1886 EOTF (0 = ideal black).',
  },
  'photometry.hlgOotf': {
    title: 'HLG OOTF',
    text: 'Apply the BT.2100 system gamma to convert scene light to display light in cd/m².',
    how: 'Off: values are scene-linear (relative).',
  },
  'photometry.hlgPeak': { title: 'HLG nominal peak', text: 'Display peak luminance for the HLG OOTF (system gamma 1.2 at 1000 cd/m²).' },
  'photometry.primaries': {
    title: 'Colour primaries',
    text: 'The RGB colour space of the linear data. Determines the luminance weights and all colorimetric quantities.',
  },
  'photometry.primariesSelect': {
    title: 'Primaries',
    text: 'Choose the RGB primaries and white point.',
    how: 'sRGB/BT.709 for most content; Display P3 for Apple wide-gamut captures; BT.2020 for HDR video; Adobe RGB / ProPhoto for photo workflows.',
  },
  'photometry.custom': {
    title: 'Custom primaries',
    text: 'CIE 1931 xy chromaticities of the red, green and blue primaries and the white point.',
  },
  'photometry.weights': {
    title: 'Luminance weights',
    text: 'Y = weighted sum of linear R, G, B — the middle row of the RGB→XYZ matrix derived from the primaries.',
    how: 'For sRGB/BT.709 this is the familiar 0.2126 / 0.7152 / 0.0722.',
  },
  'photometry.darkflat': {
    title: 'Dark & flat correction',
    text: 'Sensor corrections applied in the linear domain: (image − dark) / normalised(flat − dark).',
  },
  'photometry.dark': {
    title: 'Dark correction',
    text: 'Subtract a constant offset or a full dark frame from the linear signal.',
    how: 'Use a dark frame taken with the same exposure, gain and temperature for fixed-pattern noise removal.',
  },
  'photometry.darkConstant': { title: 'Dark offset', text: 'Constant subtracted from every channel, in linear relative units.' },
  'photometry.flat': {
    title: 'Flat-field correction',
    text: 'Divide by the flat-field slot (normalised to mean 1 per channel) to remove vignetting and response non-uniformity.',
  },
  'photometry.advanced': { title: 'Advanced', text: 'Reference white for L*, saturation flagging and the float saturation level.' },
  'photometry.lstarWhite': {
    title: 'L* reference white',
    text: 'Luminance Yn that maps to L* = 100, in relative linear units (1 = encoded white).',
  },
  'photometry.clipDetection': {
    title: 'Saturation flags',
    text: 'Mark pixels at the maximum code value (e.g. 255) as saturated: shaded red in the profile, counted in metrics, shown by the saturation overlay.',
    how: 'Automatic turns this off for screen captures and pasted screenshots, where 255 is simply white content, and on for camera, RAW, file and float data where it signals clipping.',
  },
  'photometry.floatClip': {
    title: 'Float saturation level',
    text: 'Normalised value at or above which float pixels are flagged as saturated (∞ disables).',
    how: 'Integer images always use their maximum code value.',
  },

  // ------------------------------------------------------------ calibration
  'cal.photometric': {
    title: 'Photometric calibration',
    text: 'Maps relative luminance Y to a physical unit (e.g. cd/m²). Applied to Y, X, Z and linear RGB; chromaticity is unaffected.',
  },
  'cal.mode': {
    title: 'Calibration model',
    text: 'Function f(Y) from relative to calibrated values.',
    how: 'Scale: f = k·Y (linear sensor, zero offset). Gain + offset: f = g·Y + o. Polynomial: least-squares fit for mild non-linearity. Lookup table: piecewise linear through reference points.',
  },
  'cal.unit': { title: 'Calibrated unit', text: 'Unit label for calibrated values, shown on axes and in exports.' },
  'cal.fit': {
    title: 'Fit from reference points',
    text: 'Determine k (or g and o) by least squares from the reference points instead of typing them.',
  },
  'cal.scale': {
    title: 'Scale factor k',
    text: 'Calibrated value per unit of relative luminance.',
    how: 'Example: if relative Y = 0.8 corresponds to 240 cd/m², k = 300.',
  },
  'cal.gain': { title: 'Gain g', text: 'Slope of the linear calibration f(Y) = g·Y + o.' },
  'cal.offset': { title: 'Offset o', text: 'Intercept of the linear calibration, in the calibrated unit.' },
  'cal.degree': { title: 'Polynomial degree', text: 'Order of the least-squares polynomial (needs degree + 1 points).' },
  'cal.points': {
    title: 'Reference points',
    text: 'Pairs of measured relative Y and known reference values (e.g. from a spot luminance meter).',
    how: 'Measured values are uncalibrated means over the ROI and include dark/flat correction.',
  },
  'cal.addFromRoi': {
    title: 'Add point from ROI',
    text: 'Add a reference point with the mean relative Y of the selected area or point ROI.',
    how: 'Then type the known reference value in the new row.',
  },
  'cal.addManual': { title: 'Add point manually', text: 'Add an empty row to type measured and reference values.' },
  'cal.summary': {
    title: 'Calibration function',
    text: 'The fitted function with its root-mean-square residual and coefficient of determination R².',
  },
  'cal.spatial': {
    title: 'Spatial calibration',
    text: 'Converts pixel distances to physical or angular units for axes, widths, areas, pitches and frequencies.',
  },
  'cal.spatialUnit': { title: 'Spatial unit', text: 'Unit for distances. Angular units suit luminance cameras (degrees per pixel).' },
  'cal.perPixel': { title: 'Pixel size', text: 'Size of one pixel in the chosen unit (e.g. mm/px).' },
  'cal.tool': {
    title: 'Draw reference line',
    text: 'Activate the calibration tool, draw along a known distance and enter its length.',
    keys: 'C',
  },
  'cal.applyLine': { title: 'Apply', text: 'Set pixel size = known length / line length.' },
  'cal.fromFile': { title: 'Use file resolution', text: 'Use the resolution stored in the file (PNG pHYs / TIFF resolution tags).' },
  'cal.resetSpatial': { title: 'Reset', text: 'Measure distances in pixels again.' },

  // ------------------------------------------------------------ sampling & profile
  'sampling.width': {
    title: 'Band width',
    text: 'Number of parallel sample lines (1 px apart, perpendicular to the line) combined into each profile point.',
    how: 'Wider bands suppress noise (σ drops ≈ 1/√width) but blur features that are not perpendicular to the line. Drag the round handles on the line or type a value.',
  },
  'sampling.interp': {
    title: 'Interpolation',
    text: 'How values between pixel centres are computed.',
    how: 'Nearest: raw pixel values, no smoothing. Bilinear: linear between the 4 nearest pixels (ImageJ default). Bicubic: Keys a = −0.5 kernel over 16 pixels; sharper but can overshoot at edges.',
  },
  'sampling.step': {
    title: 'Sample step',
    text: 'Distance between profile samples along the line, in pixels.',
    how: '1 px matches the image resolution; smaller steps oversample (smoother curves, no new information).',
  },
  'sampling.stat': {
    title: 'Band statistic',
    text: 'How the samples across the band combine into one profile value.',
    how: 'Mean: standard noise averaging. Median: robust to hot/dead pixels and thin defects. Min/Max: extreme projection. Sum: integrated signal.',
  },
  'sampling.oob': {
    title: 'Outside the image',
    text: 'What happens to samples that fall outside the image.',
    how: 'Gap (recommended): NaN — shown as gaps and excluded from metrics. Clamp: repeat the edge pixel (fabricates data).',
  },
  'profile.line': {
    title: 'Profiled line',
    text: 'The line whose profile and metrics are shown (the selected line, or the most recent one).',
  },
  'profile.envelope': {
    title: 'Band envelope',
    text: 'Shaded band around the profile showing variation across the line width.',
    how: '±σ: sample standard deviation across the band (local noise/texture). min–max: full range across the band. Needs width > 1.',
  },
  'profile.allLines': { title: 'All lines', text: 'Overlay the profiles of every line ROI, each in its own colour.' },
  'profile.showB': { title: 'Image B overlay', text: 'Show image B’s profile along the same line (dashed grey).' },
  'profile.markers': {
    title: 'Markers',
    text: 'Show the baseline, peak positions and FWHM bars on the plot (and ticks on the line in the image).',
  },
  'profile.derivative': {
    title: 'Derivative',
    text: 'Plot d(value)/d(distance) on a secondary axis to locate edges and gradients.',
    how: 'Smoothing is set in Metrics → Analysis settings.',
  },
  'profile.normalize': {
    title: 'Normalise',
    text: 'Scale every curve so its maximum is 1, to compare shapes of curves with different levels.',
  },
  'profile.ylog': { title: 'Logarithmic y axis', text: 'Show values on a log scale (non-positive values are hidden).' },
  'profile.yrange': { title: 'Y range', text: 'Fix the y-axis limits. Leave empty for automatic scaling.' },
  'profile.pin': {
    title: 'Pin profile',
    text: 'Freeze a copy of the current profile as a dashed reference curve, e.g. before changing a setting or loading another image.',
  },
  'profile.unpin': { title: 'Clear pinned profiles', text: 'Remove all pinned reference curves.' },
  'plot.reset': { title: 'Reset zoom', text: 'Show the full x range again (or double-click the plot).' },

  // ------------------------------------------------------------ metrics
  'metrics.copy': { title: 'Copy metrics', text: 'Copy the metric and peak tables as tab-separated text (pastes into spreadsheets).' },
  'metrics.peaks': {
    title: 'Peaks',
    text: 'Detected peaks (or dips) with interpolated position and value, height above baseline, topographic prominence and full widths.',
    how: 'Positions and peak values are refined to sub-sample precision by parabolic interpolation; width crossings by cubic interpolation.',
  },
  'metrics.settings': {
    title: 'Analysis settings',
    text: 'Baseline model, peak polarity and detection, width levels, edge levels and derivative smoothing.',
  },
  'metrics.baseline': {
    title: 'Baseline',
    text: 'Reference level that peak heights and widths are measured from.',
    how: 'Minimum: lowest profile value. Zero: absolute. Profile ends: mean of the first/last samples (background). Linear fit through ends: removes a sloped background. Percentile: robust floor. Manual: your value.',
  },
  'metrics.edgeFraction': {
    title: 'End fraction',
    text: 'Share of samples at each end of the profile used for the end-based baselines and edge levels.',
  },
  'metrics.percentile': {
    title: 'Baseline percentile',
    text: 'Percentile of the profile values used as baseline (5 % ignores a few dark outliers).',
  },
  'metrics.manualBaseline': { title: 'Manual baseline', text: 'Fixed baseline value in the measured unit.' },
  'metrics.polarity': { title: 'Peaks or dips', text: 'Analyse maxima (bright lines, spots) or minima (dark lines, gaps).' },
  'metrics.peakMode': {
    title: 'Peak reporting',
    text: 'Report only the highest peak, or every peak that passes the prominence and separation criteria.',
  },
  'metrics.prominence': {
    title: 'Minimum prominence',
    text: 'How far a peak must rise above the higher of its two surrounding valleys, as a percentage of the profile range.',
    how: 'Raise it to ignore noise wiggles; lower it to catch weak peaks.',
  },
  'metrics.separation': {
    title: 'Minimum separation',
    text: 'Peaks closer than this distance are suppressed in favour of the higher one.',
  },
  'metrics.widthLevels': {
    title: 'Width levels',
    text: 'Levels (as % of peak height above baseline) at which full widths are reported.',
    how: '50 % = FWHM; 13.5 % = 1/e² (laser beams); 10 % = FW10. Enter any comma-separated percentages.',
  },
  'metrics.edgeLevels': {
    title: 'Edge levels',
    text: 'Transition levels for the edge rise distance (10–90 % by default, 20–80 % is also common).',
  },
  'metrics.smoothing': {
    title: 'Derivative smoothing',
    text: 'Gaussian smoothing (σ in samples) applied before differentiating, to tame noise.',
  },
  'metric.max': { title: 'Maximum', text: 'Largest valid profile sample (not interpolated).' },
  'metric.min': { title: 'Minimum', text: 'Smallest valid profile sample.' },
  'metric.mean': { title: 'Mean', text: 'Arithmetic mean of valid samples.' },
  'metric.median': { title: 'Median', text: '50th percentile of valid samples; robust against spikes.' },
  'metric.sd': { title: 'Standard deviation', text: 'Sample standard deviation (n − 1) of the profile values.' },
  'metric.rms': { title: 'RMS', text: 'Root mean square of the profile values.' },
  'metric.cv': { title: 'Coefficient of variation', text: 'Standard deviation / mean — a relative non-uniformity measure.' },
  'metric.michelson': {
    title: 'Michelson contrast',
    text: '(max − min) / (max + min), 0…1. Standard contrast measure for periodic patterns and line pairs.',
  },
  'metric.u0': { title: 'Overall uniformity U0', text: 'Minimum / mean (e.g. EN 13201 road lighting, display uniformity).' },
  'metric.u1': { title: 'Uniformity U1', text: 'Minimum / maximum (longitudinal uniformity, worst-case ratio).' },
  'metric.nonuni': { title: 'Non-uniformity', text: '(max − min) / mean.' },
  'metric.integral': {
    title: 'Integral',
    text: 'Area under the profile (trapezoidal rule) — proportional to the total flux across the line.',
  },
  'metric.centroid': {
    title: 'Centroid',
    text: 'Intensity-weighted centre (baseline-subtracted) — robust position estimate for spots and lines.',
  },
  'metric.baseline': { title: 'Baseline', text: 'The baseline in use (choose the model in Analysis settings).' },
  'metric.edge': {
    title: 'Edge transition',
    text: 'Distance between the low and high transition levels (e.g. 10–90 %) around the steepest part of the profile.',
    how: 'Low/high levels are the means of the profile ends. For a Gaussian-blurred edge the 10–90 % distance is 2.563 σ.',
  },
  'metric.clipped': {
    title: 'Clipped samples',
    text: 'Profile samples that touch pixels at the maximum code value. For camera data this means saturation: the true value is unknown.',
    how: 'For screen content the maximum code is ordinary white; saturation flags are then off automatically (Photometry → Advanced).',
  },

  // ------------------------------------------------------------ display
  'display.mode': {
    title: 'View mode',
    text: 'Colour: natural image. Quantity: the measured quantity in grey. False colour: the quantity through a colormap.',
    keys: 'M',
  },
  'display.compare': {
    title: 'Image / comparison',
    text: 'Show and measure A, B, or their difference, ratio or percentage change (sizes must match).',
    how: 'Comparisons are computed on the measured quantity after linearisation and calibration.',
  },
  'display.exposure': {
    title: 'Exposure',
    text: 'Brighten or darken the colour view in photographic stops (display only).',
    how: 'Useful for HDR and linear data; +1 EV doubles the displayed light.',
  },
  'display.colormap': {
    title: 'Colormap',
    text: 'Colour scale for false-colour display.',
    how: 'Viridis/Inferno/Magma/Cividis are perceptually uniform (recommended). Turbo and Jet are rainbow maps familiar from LMK-style luminance images.',
  },
  'display.bands': { title: 'Bands', text: 'Quantise the colormap into this many flat steps (iso-level banding). 0 = continuous.' },
  'display.histogram': {
    title: 'Histogram',
    text: 'Distribution of the measured values (log counts) with the display window.',
    how: 'Click or drag to move the nearest window limit.',
  },
  'display.window': { title: 'Display window', text: 'Values mapped to black/start and white/end of the colour scale.' },
  'display.auto': { title: 'Auto window', text: 'Set the window automatically from robust percentiles of the data.' },
  'display.log': {
    title: 'Log scale',
    text: 'Map values logarithmically — reveals detail across large dynamic ranges (HDR, luminance images).',
  },
  'display.minmax': { title: 'Min–max', text: 'Set the window to the full data range.' },
  'display.percentile': {
    title: 'Auto clip',
    text: 'Percentage of pixels clipped at each end by the auto window (robust against outliers).',
  },
  'display.overlays': { title: 'Overlays', text: 'Graphics drawn over the image.' },
  'display.clip': {
    title: 'Saturation overlay',
    text: 'Highlight saturated pixels (red), black-clipped pixels (blue) and pixels that are both (magenta).',
    how: 'Measurements on clipped pixels underestimate the true signal.',
  },
  'display.grid': { title: 'Pixel grid', text: 'Draw pixel boundaries when zoomed in beyond ~800 %.' },
  'display.values': { title: 'Pixel values', text: 'Print the measured value in each pixel when zoomed in far enough (≈ 5000 %).' },
  'display.labels': { title: 'ROI labels', text: 'Show ROI names next to the shapes.' },
  'display.scalebar': { title: 'Scale bar', text: 'Show a scale bar in the current spatial unit.' },
  'display.legend': { title: 'Legend', text: 'Show the colour scale with its value range and unit (quantity and false-colour views).' },
  'display.contours': {
    title: 'Iso-contours',
    text: 'Lines of equal value (iso-luminance / isocandela-style) over the image.',
    how: 'Computed by marching squares on the measurement plane. Very large images are subsampled for speed.',
  },
  'display.contourLevels': { title: 'Contour levels', text: 'Evenly spaced across the display window, or an explicit list.' },
  'display.contourCount': { title: 'Number of levels', text: 'How many evenly spaced contour levels to draw inside the window.' },
  'display.contourLog': { title: 'Log spacing', text: 'Space levels logarithmically (equal ratios), e.g. for luminance falloff.' },
  'display.contourList': { title: 'Contour values', text: 'Comma-separated values in the measured unit.' },
  'display.contourColor': { title: 'Contour colour', text: 'Colour of the contour lines and labels.' },
  'display.contourLabels': { title: 'Contour labels', text: 'Print each contour’s value on the image.' },

  // ------------------------------------------------------------ ROIs
  'roi.color': { title: 'ROI colour', text: 'Colour used for the shape and its profile curve.' },
  'roi.name': { title: 'ROI name', text: 'Name used in the plot legend, tables and exports.' },
  'roi.visible': { title: 'Visibility', text: 'Show or hide the ROI (hidden lines are still profiled).' },
  'roi.lock': { title: 'Lock', text: 'Prevent accidental moving or reshaping.' },
  'roi.center': { title: 'Centre view', text: 'Pan the image so this ROI is in the middle of the viewport.' },
  'roi.duplicate': { title: 'Duplicate', text: 'Copy this ROI (offset by 10 px).', keys: '⌘D' },
  'roi.delete': { title: 'Delete', text: 'Remove this ROI.', keys: 'Del' },
  'roi.clear': { title: 'Delete all', text: 'Remove all ROIs (undoable).' },
  'roi.geometry': {
    title: 'Geometry',
    text: 'Exact ROI coordinates in image pixels. Pixel (i, j) spans [i, i+1) × [j, j+1); its centre is (i + 0.5, j + 0.5).',
    how: 'Use ↑/↓ in a field to step (Shift ×10, Alt ×0.1). Line angle is counter-clockwise from +x.',
  },
  'roi.copyStats': { title: 'Copy statistics', text: 'Copy the area statistics as tab-separated text.' },

  // ------------------------------------------------------------ inspector
  'inspector.freeze': { title: 'Freeze position', text: 'Keep inspecting the current pixel while the mouse moves elsewhere.', keys: 'I' },
  'inspector.value': {
    title: 'Measured value',
    text: 'The current quantity at this pixel (after linearisation, corrections and calibration).',
  },
  'inspector.codes': {
    title: 'Code values',
    text: 'Raw stored numbers per channel, before any processing (the denominator is the maximum code).',
  },
  'inspector.linear': { title: 'Linear RGB', text: 'Values after the EOTF and dark/flat correction, proportional to light.' },
  'inspector.xyz': {
    title: 'XYZ',
    text: 'CIE 1931 tristimulus values (relative, uncalibrated), from linear RGB via the primaries matrix.',
  },
  'inspector.chroma': {
    title: 'Chromaticity',
    text: 'CIE 1931 x, y and correlated colour temperature (when within Duv ±0.05 of the Planckian locus).',
  },
  'inspector.loupe': { title: 'Loupe', text: 'Numeric neighbourhood of the cursor pixel (centre outlined), shaded by the display window.' },

  // ------------------------------------------------------------ analysis
  'analysis.tabs': { title: 'Analysis type', text: 'Spectrum of the selected profile, or MTF from a slanted edge in a rectangle ROI.' },
  'spectrum.window': {
    title: 'Window function',
    text: 'Taper applied before the FFT to reduce spectral leakage.',
    how: 'Hann is a good default; Blackman has lower leakage but wider peaks; Rectangular gives the sharpest peak for exactly periodic segments.',
  },
  'spectrum.detrend': { title: 'Detrend', text: 'Remove the mean or a linear trend so slow variations do not swamp the spectrum.' },
  'spectrum.zeropad': {
    title: 'Zero padding',
    text: 'Interpolate the spectrum on a finer frequency grid (does not add resolution, improves peak location).',
  },
  'spectrum.dominant': {
    title: 'Dominant frequency',
    text: 'Strongest spectral peak (refined by parabolic interpolation), ignoring the lowest bins.',
  },
  'spectrum.period': {
    title: 'Period / pitch',
    text: '1 / dominant frequency — e.g. pixel pitch, LED spacing, line-pair period or mura wavelength.',
  },
  'spectrum.amp': { title: 'Amplitude', text: 'Amplitude of the dominant component: a sinusoid of amplitude A reads ≈ A.' },
  'spectrum.used': { title: 'Samples analysed', text: 'Longest gap-free run of the profile used for the FFT.' },
  'spectrum.nyquist': { title: 'Nyquist frequency', text: 'Highest frequency representable at the current sample step: 1 / (2 · step).' },
  'spectrum.export': { title: 'Export spectrum', text: 'Download frequency and amplitude as CSV.' },
  'mtf.roi': {
    title: 'Edge ROI',
    text: 'Rectangle containing one straight, slightly slanted edge (about 2–10° from vertical or horizontal).',
    how: 'The slant lets rows sample the edge at different sub-pixel phases, giving a 4× oversampled edge spread function (ISO 12233 method).',
  },
  'mtf.axis': { title: 'Frequency unit', text: 'Cycles per pixel, or cycles per spatial unit (needs spatial calibration).' },
  'mtf.mtf50': { title: 'MTF50', text: 'Frequency where contrast drops to 50 % — the most common single-number sharpness metric.' },
  'mtf.mtf10': { title: 'MTF10', text: 'Frequency where contrast drops to 10 % (≈ limiting resolution).' },
  'mtf.nyquist': { title: 'MTF at Nyquist', text: 'Contrast at 0.5 cycles/pixel. High values indicate aliasing risk.' },
  'mtf.angle': { title: 'Edge angle', text: 'Fitted edge angle relative to the sampling axis.' },
  'mtf.rows': { title: 'Rows used', text: 'Number of rows (or columns) crossing the edge that contributed to the ESF.' },
  'mtf.export': { title: 'Export MTF', text: 'Download the MTF curve as CSV.' },

  // ------------------------------------------------------------ export
  'export.profile': { title: 'Profile data', text: 'Numerical exports of profiles, metrics and ROI statistics.' },
  'export.profileCsv': {
    title: 'Profile CSV',
    text: 'Every sample of the selected profile: distance, position, value, band σ/min/max/count, clipping flag, derivative and image-B values.',
    how: 'RFC 4180 CSV, UTF-8, “.” decimal separator; the header lines (#) record the full pipeline for traceability.',
  },
  'export.allProfiles': { title: 'All profiles', text: 'One CSV file per line ROI.' },
  'export.metricsCsv': {
    title: 'Metrics CSV',
    text: 'All metrics and peak tables of all lines in long format (line, metric, value, unit).',
  },
  'export.areaCsv': { title: 'Area statistics CSV', text: 'Statistics and colorimetry of every area and point ROI.' },
  'export.report': {
    title: 'JSON report',
    text: 'Everything in one machine-readable file: settings, pipeline, lines with metrics and profiles, area statistics.',
  },
  'export.chart': { title: 'Charts', text: 'The profile plot as a high-resolution PNG or a vector SVG (for reports and publications).' },
  'export.chartSize': { title: 'Chart size', text: 'Chart dimensions in CSS pixels (points in the SVG).' },
  'export.chartScale': { title: 'PNG scale', text: 'Pixel density multiplier for the PNG (2 = retina, 4 = print).' },
  'export.chartPng': { title: 'Chart PNG', text: 'Raster image of the profile plot.' },
  'export.chartSvg': { title: 'Chart SVG', text: 'Vector image of the profile plot, editable in Illustrator/Inkscape.' },
  'export.images': { title: 'Images', text: 'Annotated images and the raw measurement plane.' },
  'export.annotated': {
    title: 'Annotated image',
    text: 'Full-resolution image with the current display mapping, ROIs, contours, scale bar and legend.',
  },
  'export.view': { title: 'Current view', text: 'Screenshot of the viewport exactly as displayed.' },
  'export.tiff': {
    title: 'Float TIFF',
    text: 'The measurement plane (current quantity, after linearisation and calibration) as a single-channel 32-bit float TIFF.',
    how: 'Opens in ImageJ/FIJI, MATLAB, Python; the pipeline description is stored in the TIFF ImageDescription tag.',
  },
  'export.pfm': { title: 'PFM', text: 'The measurement plane as a Portable Float Map (simple 32-bit float format).' },
  'export.session': {
    title: 'Session',
    text: 'Save everything — images, ROIs, calibrations, settings — into one .aperto file, and load it later.',
  },
  'export.embed': {
    title: 'Embed images',
    text: 'Include the exact image data (all slots) in the session file. Turn off for small settings-only files.',
  },
  'export.saveSession': { title: 'Save session', text: 'Download a .aperto file (zip) with the current state.', keys: '⌘S' },
  'export.loadSession': { title: 'Load session', text: 'Restore a .aperto session file. You can also drop it on the window.' },
};
